// Действия из Mini App: формы и справочники.
import { z } from "zod";
import type { TransactionDto } from "./dashboard.ts";

export interface CategoryOptionDto {
  code: string;
  kind: "income" | "expense";
  name: string;
  icon: string;
}

export interface AccountOptionDto {
  id: string;
  name: string;
  currency: "UZS" | "USD";
  personal: boolean;
  /** Основной: сюда записывается всё, где счёт не выбран. */
  isDefault: boolean;
  /** Заначка: личный счёт вне общего остатка. */
  stash: boolean;
  /** С какого дня счёт ведут (YYYY-MM-DD, Ташкент): деньги кредита, взятого раньше, в остаток не идут. */
  since: string;
}

export interface FamilyMemberDto {
  userId: string;
  name: string;
  role: "admin" | "adult" | "child";
  username: string | null;
  isMe: boolean;
}

/** Справочники для форм Mini App. */
/**
 * Версия кода для людей: «e2f83f1 · 04.10.2026». В VERSION её подставляет git archive (коммит и дата);
 * без подстановки (разработка, сборка не из архива) — «dev».
 */
export function displayVersion(raw: string): string {
  const m = raw.trim().match(/^([0-9a-f]{7,40}) (\d{4})-(\d{2})-(\d{2})$/);
  return m ? `${m[1]!.slice(0, 7)} · ${m[4]}.${m[3]}.${m[2]}` : "dev";
}

export interface MetaDto {
  /** Версия сервера (см. displayVersion): Mini App сверяет со своей — видно, что Telegram показал старую копию. */
  version: string;
  categories: CategoryOptionDto[];
  accounts: AccountOptionDto[];
  members: FamilyMemberDto[];
  botUsername: string | null;
  /** Анкета семьи: ожидаемый доход, день зарплаты, пройдена ли настройка. */
  family: { monthlyIncome: number | null; incomeDay: number | null; setupDone: boolean };
}

export const FinanceProfileSchema = z.object({
  monthlyIncome: z.number().positive().max(1e12).nullable(),
  incomeDay: z.number().int().min(1).max(31).nullable(),
});

export interface RecurringDto {
  id: string;
  name: string;
  amount: number;
  currency: "UZS" | "USD";
  dayOfMonth: number;
  nextDueDate: string;
  categoryCode: string | null;
}

/** Черновик операции — тот же формат, что у разбора текста в боте. */
export const DraftSchema = z.object({
  type: z.enum(["expense", "income", "transfer"]),
  amount: z.number().positive().max(1e12),
  currency: z.enum(["UZS", "USD"]),
  category: z.string().nullable(),
  transferToUserId: z.string().uuid().nullable().default(null),
  scope: z.enum(["personal", "shared"]).default("shared"),
  comment: z.string().trim().max(200).nullable().default(null),
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  items: z
    .array(z.object({ name: z.string().trim().min(1).max(120), qty: z.number().nullable(), unit: z.string().nullable(), amount: z.number().nullable() }))
    .max(100)
    .default([]),
  /** Id записи, созданный в приложении: повтор из офлайн-очереди не создаёт дубль. */
  clientId: z.string().uuid().optional(),
  /** Счёт: расход — откуда, доход — куда, перевод — откуда. null — основной счёт (или личный — для личной операции). */
  accountId: z.string().uuid().nullable().default(null),
  /** Перевод между своими счетами — куда. */
  toAccountId: z.string().uuid().nullable().default(null),
  /** Обмен валюты (счёт «куда» в другой валюте): сколько пришло; null — по курсу ЦБ. */
  toAmount: z.number().positive().max(1e12).nullable().default(null),
});
export type DraftInput = z.input<typeof DraftSchema>;

export const CreateTransactionsSchema = z.object({
  drafts: z.array(DraftSchema).min(1).max(20),
  /** text — фраза, разобранная ИИ («✨ Написать фразой»), как сообщение боту. */
  source: z.enum(["manual", "text"]).default("manual"),
});

export const CreateGoalSchema = z.object({
  name: z.string().trim().min(1).max(60),
  target: z.number().positive().max(1e12),
  currency: z.enum(["UZS", "USD"]).default("UZS"),
  deadline: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().default(null),
  /** null — семейная копилка. */
  ownerUserId: z.string().uuid().nullable().default(null),
});

export const ContributeSchema = z.object({ amount: z.number().refine((v) => v !== 0 && Math.abs(v) <= 1e12) });

export const CreateAccountSchema = z.object({
  name: z.string().trim().min(1).max(60),
  currency: z.enum(["UZS", "USD"]).default("UZS"),
  personal: z.boolean().default(false),
  /** Заначка — всегда личная. */
  stash: z.boolean().default(false),
});

export const StashSchema = z.object({ stash: z.boolean() });

export const CreateRecurringSchema = z.object({
  name: z.string().trim().min(1).max(60),
  amount: z.number().positive().max(1e12),
  currency: z.enum(["UZS", "USD"]).default("UZS"),
  dayOfMonth: z.number().int().min(1).max(31),
});

/** Правка сохранённой операции: перенести на другой счёт (выбрали не тот) и/или «личная / общая». */
export const UpdateTransactionSchema = z
  .object({ accountId: z.string().uuid().optional(), scope: z.enum(["personal", "shared"]).optional() })
  .refine((v) => v.accountId !== undefined || v.scope !== undefined);

/** Сверить остаток: сколько на счёте на самом деле. */
export const AccountBalanceSchema = z.object({ balance: z.number().min(-1e12).max(1e12) });

export const RenameAccountSchema = z.object({ name: z.string().trim().min(1).max(60) });

/** Счёт подробно: остаток, из чего он сложился, последние операции. */
export interface AccountDetailDto {
  id: string;
  name: string;
  currency: "UZS" | "USD";
  personal: boolean;
  isDefault: boolean;
  stash: boolean;
  balance: number;
  /**
   * Из чего сложился остаток, в валюте счёта: opening — начальный остаток и сверки, income / expense — доходы
   * и расходы, transfersIn / transfersOut — переводы на счёт и со счёта, credits — деньги кредитов,
   * goals — отложено в копилки (минус).
   */
  parts: { opening: number; income: number; expense: number; transfersIn: number; transfersOut: number; credits: number; goals: number };
  /** Последние операции по счёту, новые сверху. */
  operations: TransactionDto[];
}

/** Ответ разбора свободного текста (Mini App → «Написать текстом»). */
export interface ParsedDraftDto {
  type: "expense" | "income" | "transfer";
  amount: number | null;
  currency: "UZS" | "USD";
  category: string | null;
  transferToUserId: string | null;
  transferToName: string | null;
  scope: "personal" | "shared";
  comment: string | null;
  date: string;
  items: { name: string; qty: number | null; unit: string | null; amount: number | null }[];
  /** Счёт, названный во фразе («зарплата UZKEK»); null — основной. */
  accountId: string | null;
  accountName: string | null;
  /** Перевод между своими счетами — куда. */
  toAccountId: string | null;
  toAccountName: string | null;
  /** Обмен валюты: сколько пришло на счёт «куда»; null — по курсу ЦБ. */
  toAmount: number | null;
}
